import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '../ui/Modal';
import { FormActions, primaryButtonClass, secondaryButtonClass } from '../ui/FormControls';
import { Quote, QuoteConversionResult } from '../../types/quote.types';
import { ATPResult } from '../../types/atp.types';
import { CustomerCreditInfo, CreditCheckResult } from '../../types/credit.types';
import { formatCurrency } from '../../types/credit.types';
import { atpService } from '../../services/atp.service';
import { creditService } from '../../services/credit.service';

interface QuoteConversionModalProps {
  isOpen: boolean;
  onClose: () => void;
  quote?: Quote;
  onConfirm?: () => Promise<QuoteConversionResult>;
  onCancel?: () => void;
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const getErrorMessage = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
};

const QuoteConversionModal: React.FC<QuoteConversionModalProps> = ({
  isOpen,
  onClose,
  quote,
  onConfirm,
  onCancel,
}) => {
  const [checking, setChecking] = useState(false);
  const [converting, setConverting] = useState(false);
  const [atpChecks, setAtpChecks] = useState<ATPResult[]>([]);
  const [creditCheck, setCreditCheck] = useState<CreditCheckResult | null>(null);
  const [creditInfo, setCreditInfo] = useState<CustomerCreditInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversionResult, setConversionResult] = useState<QuoteConversionResult | null>(null);

  const quoteId = quote?.id;

  // Run the checks once each time the dialog opens for a quote
  useEffect(() => {
    if (isOpen && quoteId) {
      setConversionResult(null);
      performPreChecks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, quoteId]);

  const performPreChecks = async () => {
    if (!quote) return;

    try {
      setChecking(true);
      setError(null);
      setAtpChecks([]);
      setCreditCheck(null);
      setCreditInfo(null);

      // Stock check for every item; the quote itself is left out of the commitments
      const atpResults = await Promise.all(
        quote.items.map(item =>
          atpService.checkATP({
            productId: item.productId,
            quantity: item.quantity,
            requiredDate: quote.expectedDeliveryDate,
            excludeOrderId: quote.id,
          })
        )
      );

      // Get customer credit information and check order amount
      const [creditInfoResult, creditCheckResult] = await Promise.all([
        creditService.getCustomerCreditInfo(quote.customerId),
        creditService.checkOrderCreditLimit(quote.customerId, quote.grandTotal)
      ]);

      setAtpChecks(atpResults);
      setCreditInfo(creditInfoResult);
      setCreditCheck(creditCheckResult);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to perform pre-conversion checks'));
    } finally {
      setChecking(false);
    }
  };

  const handleConfirmConversion = async () => {
    if (!onConfirm) return;

    try {
      setConverting(true);
      setError(null);
      const result = await onConfirm();
      setConversionResult(result);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to convert quote to order'));
    } finally {
      setConverting(false);
    }
  };

  const hasStockIssues = atpChecks.some(check => !check.isAvailable);
  const hasCreditIssues = !!creditCheck && !creditCheck.canProceed;
  const checksLoaded = atpChecks.length > 0 || !!creditCheck;

  if (!quote) return null;

  if (conversionResult) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Conversion Result" size="md">
        <div
          className={`flex items-start gap-3 rounded-lg border p-4 ${
            conversionResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
          }`}
        >
          {conversionResult.success ? (
            <CheckCircleIcon className="h-7 w-7 shrink-0 text-green-600" />
          ) : (
            <XCircleIcon className="h-7 w-7 shrink-0 text-red-600" />
          )}
          <div>
            <h3
              className={`text-base font-semibold ${
                conversionResult.success ? 'text-green-800' : 'text-red-800'
              }`}
            >
              {conversionResult.success ? 'Quote converted to an order' : 'Conversion failed'}
            </h3>
            {conversionResult.message && (
              <p className={`mt-1 text-sm ${conversionResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {conversionResult.message}
              </p>
            )}
            {conversionResult.success && conversionResult.orderNumber && (
              <p className="mt-1 text-sm text-green-700">
                Order number: <span className="font-mono font-semibold">{conversionResult.orderNumber}</span>
              </p>
            )}
          </div>
        </div>
        <FormActions>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Close
          </button>
        </FormActions>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !converting && onClose()}
      title="Convert Quote to Order"
      description="Stock and credit are checked before the quote becomes an order."
      size="lg"
    >
      <div className="space-y-5">
        {/* Quote Summary */}
        <dl className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-gray-500">Quote number</dt>
            <dd className="font-mono font-medium text-gray-900">{quote.orderNumber}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Customer</dt>
            <dd className="font-medium text-gray-900">{quote.customer.name}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Amount</dt>
            <dd className="font-medium text-gray-900 tabular-nums">{formatCurrency(quote.grandTotal)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Expected delivery</dt>
            <dd className="font-medium text-gray-900">{formatDate(quote.expectedDeliveryDate)}</dd>
          </div>
        </dl>

        {checking && (
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-gray-500">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-indigo-600"></div>
            Checking stock and credit...
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
            <XCircleIcon className="h-5 w-5 shrink-0 text-red-500" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Something went wrong</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              {!checksLoaded && !checking && (
                <button
                  type="button"
                  onClick={performPreChecks}
                  className="mt-2 text-sm font-medium text-red-800 underline hover:text-red-900"
                >
                  Run the checks again
                </button>
              )}
            </div>
          </div>
        )}

        {!checking && checksLoaded && (
          <>
            {/* Stock Availability Check */}
            <div>
              <div className="mb-2 flex items-center">
                <CubeIcon className="mr-2 h-5 w-5 text-gray-400" />
                <h4 className="text-sm font-medium text-gray-700">Stock availability</h4>
              </div>
              <div className="space-y-2">
                {atpChecks.map((check, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between rounded border p-3 ${
                      check.isAvailable ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <div className="flex items-center">
                      {check.isAvailable ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-600" />
                      )}
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{check.productName}</p>
                        <p className="text-sm text-gray-600">
                          Required: {check.requestedQuantity} · Available: {check.availableToPromise}
                          {!check.isAvailable && ` · Short by ${check.shortageQuantity}`}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`text-sm font-medium ${check.isAvailable ? 'text-green-700' : 'text-red-700'}`}
                    >
                      {check.isAvailable ? 'Available' : 'Insufficient'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Credit Limit Check */}
            {creditCheck && creditInfo && (
              <div>
                <div className="mb-2 flex items-center">
                  <BanknotesIcon className="mr-2 h-5 w-5 text-gray-400" />
                  <h4 className="text-sm font-medium text-gray-700">Credit limit</h4>
                </div>
                <div
                  className={`rounded border p-4 ${
                    creditCheck.canProceed ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'
                  }`}
                >
                  <div className="flex items-start">
                    {creditCheck.canProceed ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    ) : (
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                    )}
                    <div className="ml-3 flex-1">
                      <p
                        className={`text-sm font-medium ${
                          creditCheck.canProceed ? 'text-green-800' : 'text-yellow-800'
                        }`}
                      >
                        {creditCheck.canProceed ? 'Within the credit limit' : 'Credit limit exceeded'}
                      </p>
                      <dl className="mt-2 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <dt className="text-xs text-gray-500">Credit limit</dt>
                          <dd className="font-medium text-gray-900 tabular-nums">
                            {formatCurrency(creditCheck.creditLimit)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Current outstanding</dt>
                          <dd className="font-medium text-gray-900 tabular-nums">
                            {formatCurrency(creditCheck.currentOutstanding)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500">Available credit</dt>
                          <dd
                            className={`font-medium tabular-nums ${
                              creditCheck.availableCredit > 0 ? 'text-gray-900' : 'text-red-700'
                            }`}
                          >
                            {formatCurrency(creditCheck.availableCredit)}
                          </dd>
                        </div>
                      </dl>
                      {creditCheck.exceedAmount > 0 && (
                        <p className="mt-2 text-sm text-yellow-800">
                          Exceeds the limit by {formatCurrency(creditCheck.exceedAmount)}.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {hasStockIssues && (
              <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
                <XCircleIcon className="h-5 w-5 shrink-0 text-red-500" />
                <p className="text-sm text-red-700">
                  This quote cannot be converted until every item is in stock. Receive more stock or
                  edit the quantities, then try again.
                </p>
              </div>
            )}

            {!hasStockIssues && hasCreditIssues && (
              <div className="flex items-start gap-3 rounded-md border border-yellow-200 bg-yellow-50 p-4">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-yellow-500" />
                <p className="text-sm text-yellow-800">
                  The customer will be over their credit limit. You can still convert the quote.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <FormActions>
        <button
          type="button"
          onClick={onCancel || onClose}
          className={secondaryButtonClass}
          disabled={converting}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirmConversion}
          disabled={checking || converting || !checksLoaded || hasStockIssues}
          className={primaryButtonClass}
        >
          {converting ? 'Converting...' : hasCreditIssues && !hasStockIssues ? 'Convert Anyway' : 'Convert to Order'}
        </button>
      </FormActions>
    </Modal>
  );
};

export default QuoteConversionModal;
